import { HTMLInputTypeAttribute, KeyboardEvent } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { clsx } from 'clsx';
import { getNestedErrorProperty } from '@lib/util';

interface IProps {
  className?: string;
  disabled?: boolean;
  inputClassName?: string;
  label?: string;
  labelDescription?: string;
  name: string;
  placeholder?: string;
  required?: boolean;
  type?: HTMLInputTypeAttribute | undefined;
  // value?: string | number | readonly string[] | undefined;
  valueAsNumber?: boolean;
  onBlur?: () => void;
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
  inputMode?: 'email' | 'search' | 'tel' | 'text' | 'url' | 'numeric' | 'none' | 'decimal' | undefined;
  pattern?: string;
  min?: number;
  max?: number;
  step?: string | number;
  append?: string;
  onChange?: (v: string) => void;
  onFocus?: () => void;
  shouldShowError?: boolean;
  maxLength?: number;
}

export const InputComponent = (props: IProps) => {
  const {
    control,
    formState: { errors },
  } = useFormContext(); // retrieve all hook methods

  // CHECK IF ERROR MESSAGE EXISTS
  const errorMessage = getNestedErrorProperty(errors, props.name)?.message;

  return (
    <div className={clsx(props.className)}>
      {props.label && (
        <label className="block text-sm font-medium my-2 pr-2 leading-5 text-gray-900">
          {props.label}

          {/* SHOW REQUIRED ASTERIX */}
          {props.required && <abbr className="text-red-500"> *</abbr>}
          {props.labelDescription && <span className="block text-xs font-normal text-gray-700 mt-2">{props.labelDescription}</span>}
        </label>
      )}
      <div className="relative">
        {/* INPUT FIELD */}
        <Controller
          name={props.name}
          control={control}
          render={({ field }) => (
            <input
              {...field}
              value={field.value ?? ''}
              type={props.type ?? 'text'}
              inputMode={props.inputMode}
              pattern={props.pattern}
              disabled={props.disabled}
              className={clsx([
                'block w-full rounded-sm border-0 py-1.5 ring-gray-300 ring-1 ring-inset focus:ring-1 focus:ring-inset sm:text-sm sm:leading-5 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500 disabled:ring-gray-200',
                errorMessage ? 'text-red-900 ring-red-300 placeholder:text-red-300 focus:ring-red-500' : '',
                props.inputClassName,
              ])}
              placeholder={props.placeholder}
              onBlur={props.onBlur}
              onKeyDown={props.onKeyDown}
              min={props.min}
              max={props.max}
              step={props.step}
              onChange={(e) => (props.onChange ? props.onChange(e.target.value) : field.onChange(e.target.value))}
              lang={props.inputMode === 'numeric' ? 'de' : undefined}
              onFocus={props.onFocus}
              maxLength={props.maxLength}
            />
          )}
        />
        {props.append && (
          <div className="pointer-events-none absolute inset-y-0 right-0 pr-2 text-gray-500 flex items-center">{props.append}</div>
        )}
      </div>

      {/* DISPLAY ERROR MESSAGE */}
      {props.shouldShowError ?? (errorMessage && <span className="mt-2 text-sm text-red-600">{errorMessage}</span>)}
    </div>
  );
};
